import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { api } from "../App";
import { Button } from "../components/ui/button";
import PageHeader from "../components/PageHeader";
import RangeCard from "./ranges/RangeCard";

export default function RangesPage() {
  const [ranges, setRanges] = useState([]);
  const [refreshing, setRefreshing] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await api.get("/ranges");
      setRanges(res.data || []);
    } catch {
      toast.error("Failed to load ranges");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = async (slug) => {
    setRefreshing((s) => ({ ...s, [slug]: true }));
    try {
      const res = await api.post(`/ranges/${encodeURIComponent(slug)}/refresh`);
      setRanges((list) => list.map((r) => (r.slug === slug ? res.data : r)));
      toast.success(`Refreshed ${slug}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Refresh failed for ${slug}`);
    } finally {
      setRefreshing((s) => ({ ...s, [slug]: false }));
    }
  };

  const cachedCount = ranges.filter((r) => r.cached).length;

  return (
    <div className="p-6 sm:p-8 md:p-12 max-w-7xl mx-auto w-full">
      <PageHeader
        eyebrow="DEP sources"
        title="Ranges & PDF cache"
        subtitle={`Each Collier folio prefix window has its own DEP PDF. ${cachedCount} of ${ranges.length} cached locally.`}
      >
        <Button
          onClick={load}
          variant="outline"
          className="border-border/60 hover:bg-[#F4F5F4]"
          data-testid="ranges-reload-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Reload list
        </Button>
      </PageHeader>

      <div
        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
        data-testid="ranges-grid"
      >
        {ranges.map((r) => (
          <RangeCard
            key={r.slug}
            r={r}
            refreshing={!!refreshing[r.slug]}
            onRefresh={refresh}
          />
        ))}
      </div>
    </div>
  );
}
